import { I18n } from '@calendly/i18n'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { MESSAGE_STATUS, QUERY_STATUS, QUERY_SOURCE_TYPE, isUserMessage } from 'components/agent_hub_components'
import { AI_APP_PATH } from 'features/agent_hub/ai_chat_discovery/constants'
import { useSubmitQueryMutation } from 'features/agent_hub/graphql_api'

import { useMessages } from './useMessages'
import { useQueryPolling } from './useQueryPolling'

/**
 * Main orchestration hook for GraphQL chat functionality
 * Coordinates message management, query submission, and polling
 * Optional `options.onCommitThreadId`: if set, called with the new thread id instead of updating
 * the URL (e.g. embedded right rail stores `threadId` in pane state via `openOrReplace`). Otherwise
 * the URL is updated to `/app/ai?threadId=…`.
 */
export const useGraphQLChat = (
  initialQuery,
  initialQuerySourceType = QUERY_SOURCE_TYPE.TEXT,
  analyticsHandlers = {},
  initialMessages = [],
  initialThreadId = null,
  options = {}
) => {
  const { onCommitThreadId } = options
  const initialMessageSentRef = useRef(false)
  const queryStartTimeRef = useRef(null)
  const currentQuerySourceTypeRef = useRef(QUERY_SOURCE_TYPE.TEXT)
  const history = useHistory()

  // Initialize hooks
  const { messages, addUserMessage, addAssistantMessage, shouldShowLoading } = useMessages(initialMessages)
  const { queryStatusData, queryStatusError, startPolling, stopPolling, isPolling } = useQueryPolling()
  const [threadId, setThreadId] = useState(initialThreadId)
  const [currentQueryId, setCurrentQueryId] = useState(null)
  const [
    triggerSubmitQuery,
    { data: submitQueryData, isLoading: submitQueryIsLoading, error: submitQueryError }
  ] = useSubmitQueryMutation()

  const sendSearchRespondedAnalytics = useCallback((latency) => {
    if (analyticsHandlers.trackAiResponded) {
      analyticsHandlers.trackAiResponded(latency, currentQuerySourceTypeRef.current)
    }
  }, [analyticsHandlers])

  // Error messages based on error codes
  const getErrorMessageByCode = useCallback((errorCode) => {
    switch (errorCode) {
      case 422:
        return I18n.t('publisher.agent_hub.errors.unprocessable_content')
      case 504:
        return I18n.t('publisher.agent_hub.errors.timeout')
      case 500:
        return I18n.t('publisher.agent_hub.errors.server_error')
      default:
        return I18n.t('publisher.agent_hub.errors.default')
    }
  }, [])

  // Determine if error is a client error (4xx)
  const isGuardrailError = useCallback((errorCode) => errorCode === 422, [])

  // Error handling
  const handleError = useCallback((errorCode) => {
    // Reset timer on error
    if (queryStartTimeRef.current) {
      queryStartTimeRef.current = null
    }

    const errorResponse = getErrorMessageByCode(errorCode)
    const status = isGuardrailError(errorCode) ? MESSAGE_STATUS.SUCCESS : MESSAGE_STATUS.ERROR
    addAssistantMessage(errorResponse, status, null)
    stopPolling()
  }, [addAssistantMessage, stopPolling, getErrorMessageByCode, isGuardrailError])

  // Handle sending a new message
  const handleSendMessage = useCallback((message, querySourceType = QUERY_SOURCE_TYPE.TEXT) => {
    if (submitQueryIsLoading) return
    if (!message || !message.trim()) return

    // Store the query source type for analytics
    currentQuerySourceTypeRef.current = querySourceType

    // Start timing the query latency
    queryStartTimeRef.current = Date.now()

    setCurrentQueryId(null)
    addUserMessage(message)
    triggerSubmitQuery({ query: message, threadId })
  }, [submitQueryIsLoading, addUserMessage, triggerSubmitQuery, threadId])

  // Effect for sending initial message
  useEffect(() => {
    if (!initialMessageSentRef.current && initialQuery) {
      initialMessageSentRef.current = true
      handleSendMessage(initialQuery, initialQuerySourceType)
    }
  }, [initialQuery, initialQuerySourceType, handleSendMessage])

  // Effect for starting polling when loading a thread with pending query
  useEffect(() => {
    if (initialThreadId && messages.length > 0) {
      const lastMessage = messages[messages.length - 1]
      // If the last message is from user and we have a threadId, we need to start polling
      if (isUserMessage(lastMessage) && !isPolling && lastMessage.queryId) {
        // Set currentQueryId to match the query we're polling
        setCurrentQueryId(lastMessage.queryId)
        // Start polling with the queryId from the last user message
        startPolling(lastMessage.queryId)
      }
    }
  }, [initialThreadId, messages, isPolling, startPolling])

  // Effect for handling submit query errors
  useEffect(() => {
    if (submitQueryError) {
      handleError()
    }
  }, [submitQueryError, handleError])

  // Effect for handling query status errors
  useEffect(() => {
    if (queryStatusError) {
      handleError()
    }
  }, [queryStatusError, handleError])

  // Effect for starting polling when a new query is submitted
  useEffect(() => {
    if (submitQueryData?.submitQuery?.queryId) {
      const queryId = submitQueryData.submitQuery.queryId
      const newThreadId = submitQueryData.submitQuery.threadId

      setCurrentQueryId(queryId)
      startPolling(queryId)
      setThreadId(newThreadId)

      // Persist new thread id: pane state (embedded rail) or full-page AI URL
      if (newThreadId && !threadId) {
        if (typeof onCommitThreadId === 'function') {
          onCommitThreadId(newThreadId)
        } else {
          history.push(`${AI_APP_PATH}?threadId=${newThreadId}`)
        }
      }
    }
  }, [submitQueryData, startPolling, setThreadId, history, threadId, onCommitThreadId])

  // Effect for handling query status responses
  useEffect(() => {
    if (!queryStatusData?.queryStatus) return

    const status = queryStatusData.queryStatus.status
    const errorCode = queryStatusData.queryStatus?.errorCode

    // Handle errors first - errorCode can be present with any status (e.g., 422 with COMPLETED)
    if (errorCode) {
      handleError(errorCode)
    } else if (status === QUERY_STATUS.COMPLETED) {
      const content = queryStatusData.queryStatus?.content || ''
      const responseId = queryStatusData.queryStatus?.responseId

      // Calculate and log latency
      if (queryStartTimeRef.current) {
        const latency = Date.now() - queryStartTimeRef.current
        sendSearchRespondedAnalytics(latency)
        queryStartTimeRef.current = null // Reset for next query
      }

      addAssistantMessage(content, MESSAGE_STATUS.SUCCESS, queryStatusData.queryStatus?.queryId, responseId)
      stopPolling()
    } else if (status === QUERY_STATUS.FAILED) {
      // All other status FAILED without errorCode
      handleError()
    }
  }, [queryStatusData, addAssistantMessage, handleError, stopPolling, sendSearchRespondedAnalytics])

  const onCancelGeneration = () => {
    // Reset timer on cancellation
    if (queryStartTimeRef.current) {
      queryStartTimeRef.current = null
    }

    addAssistantMessage(`*${I18n.t('publisher.agent_hub.cancel_generation')}*`, MESSAGE_STATUS.CANCELLED, null)
    stopPolling()
  }

  // Extract all tool calls from queryStatusData
  // Tool calls are ordered with most recent first in the edges array
  // Only return toolCalls if they belong to the current query to prevent showing old toolCalls
  const toolCalls = (() => {
    if (!queryStatusData?.queryStatus) return []
    // Only return toolCalls if they belong to the current query
    if (queryStatusData.queryStatus?.queryId === currentQueryId) {
      return queryStatusData.queryStatus.toolCalls?.edges?.map((edge) => edge.node) || []
    }
    return []
  })()

  return {
    messages,
    handleSendMessage,
    isLoading: shouldShowLoading(),
    onCancelGeneration,
    toolCalls
  }
}
